import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { authService } from '@/services/auth.service';
import { billingService } from '@/services/billing.service';
import { beginDeviceSession, endDeviceSession } from '@/services/device-session.service';
import {
  evaluateLogoutGuard,
  type UnsyncedWorkSummary,
} from '@/services/logout-guard.service';
import { useShiftStore } from '@/stores/use-shift-store';
import {
  useCartStore,
  setCartGstRegistration,
  setLocationSupplyTypes,
  setTenantCurrency,
} from '@/stores/use-cart-store';
import { useLocationStore } from '@/stores/use-location-store';
import { cacheDiscountCap, cacheTenantCurrency, cacheTenantTaxInclusive } from '@/db/pos-database';
import { setLocationTimezones, setTenantTimezone } from '@/lib/tenant-clock';
import { setConfiguredDiscountCap } from '@/lib/discount-caps';
import { setCashRoundingSettings } from '@/lib/cash-rounding';
import type { AuthUser, UserProfile } from '@/types/auth';

const USER_KEY = 'bbpos_user';
/** Set when a logout could not reach the server (e.g. offline): the session
 *  cookie is still live, and hydrate must not adopt it again. */
const LOGOUT_PENDING_KEY = 'bbpos_logout_pending';

interface AuthState {
  user: AuthUser | null;
  profile: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;

  verifyOtp: (email: string, otp: string) => Promise<void>;
  verifyTotp: (email: string, code: string) => Promise<void>;
  /**
   * Logout. If there is unsynced work and the device is offline, the
   * returned `blocked` flag is true and the UI must show a discard/cancel
   * dialog. Call again with `{ force: true }` to discard and proceed.
   * Unsynced bills (`unsynced.pending_sync > 0`) block even a forced logout (T17).
   */
  logout: (opts?: { force?: boolean }) => Promise<{ blocked: boolean; unsynced?: UnsyncedWorkSummary }>;
  refreshSession: () => Promise<void>;
  fetchProfile: () => Promise<void>;
  hydrate: () => void;
}

export const useAuthStore = create<AuthState>()(devtools((set, get) => ({
  user: null,
  profile: null,
  isAuthenticated: false,
  isLoading: false,
  isHydrated: false,

  verifyOtp: async (email, otp) => {
    const data = await authService.verifyOtp(email, otp);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    localStorage.removeItem(LOGOUT_PENDING_KEY);
    set({
      user: data.user,
      isAuthenticated: true,
    });
    // G2.27: enroll device + install IndexedDB encryption key.
    await beginDeviceSession();
    await get().fetchProfile();
    // Recover any orphan open shift the server still has for this user
    // (e.g. previous session logged out without clocking out).
    try {
      await useShiftStore.getState().loadActiveShift(data.user.sub, '');
    } catch {
      // non-fatal
    }
  },

  verifyTotp: async (email, code) => {
    const data = await authService.verifyTotp(email, code);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    localStorage.removeItem(LOGOUT_PENDING_KEY);
    set({
      user: data.user,
      isAuthenticated: true,
    });
    // G2.27: enroll device + install IndexedDB encryption key.
    await beginDeviceSession();
    await get().fetchProfile();
    // Recover any orphan open shift the server still has for this user
    // (e.g. previous session logged out without clocking out).
    try {
      await useShiftStore.getState().loadActiveShift(data.user.sub, '');
    } catch {
      // non-fatal
    }
  },

  logout: async (opts) => {
    // G2.27: pre-logout sync-flush guard. Because endDeviceSession()
    // wipes IndexedDB, we must first check for unsynced offline work
    // that would be lost. If offline with pending rows, bail back to
    // the UI so it can prompt the user — unless `force` says otherwise.
    // T17: `force` never discards unsynced bills; the guard still blocks.
    const decision = await evaluateLogoutGuard({ force: opts?.force });
    if (!decision.canLogout && decision.reason) {
      return { blocked: true, unsynced: decision.reason.summary };
    }

    // QA GP2-003: a held server draft's claim never lapses — free it while
    // the JWT is still good, so another till can open the bill.
    const heldBillId = useCartStore.getState().currentBillId;
    if (heldBillId) await billingService.releaseBillLock(heldBillId).catch(() => undefined);

    // G2.27: invalidate the device KEK, clear the in-memory key, and
    // wipe IndexedDB. Runs before authService.logout() so we still have
    // a valid JWT to authenticate the DELETE /auth/device/:id call.
    await endDeviceSession();
    try {
      await authService.logout();
      localStorage.removeItem(LOGOUT_PENDING_KEY);
    } catch {
      // Ignore errors — clear local state regardless
      localStorage.setItem(LOGOUT_PENDING_KEY, '1');
    }
    useShiftStore.getState().clearShift();
    useCartStore.getState().resetAll();
    useLocationStore.getState().clearLocation();
    localStorage.removeItem(USER_KEY);
    set({
      user: null,
      profile: null,
      isAuthenticated: false,
    });
    // Cross-store cleanup is handled by store-wiring.ts subscription on isAuthenticated
    return { blocked: false };
  },

  refreshSession: async () => {
    try {
      const data = await authService.refreshSession();
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      set({
        user: data.user,
        isAuthenticated: true,
      });
    } catch {
      localStorage.removeItem(USER_KEY);
      set({
        user: null,
        profile: null,
        isAuthenticated: false,
      });
    }
  },

  fetchProfile: async () => {
    try {
      const profile = await authService.getMe();
      set({ profile });
      // Multi-currency: cache the tenant's immutable currency so an offline
      // reload still has the correct display/rounding currency. Best-effort —
      // a failed IndexedDB write must not block login.
      try {
        await cacheTenantCurrency(profile.tenant?.default_currency, profile.tenant_id ?? null);
        await cacheTenantTaxInclusive(profile.tenant?.show_tax_inclusive === true);
        // CGST + SGST vs IGST per store, from the GST resolver (gst-parked D7).
        setLocationSupplyTypes(profile.locations);
        setTenantCurrency(profile.tenant?.default_currency);
        // Not GST registered ⇒ no GST on the till (gst-parked 090, D5).
        setCartGstRegistration(profile.tenant?.gst_registered, profile.tenant?.default_currency);
        // Cash rounding rule (T07): tenant default + per-location overrides.
        setCashRoundingSettings(profile);
        // The tenant's own discount limit for this user's role. Held in a
        // module-level value rather than a hook because the modal reads it
        // during render; cached so an offline boot still shows the real limit.
        const servedCap = (profile as { discount_cap?: { percent: number; flat: number | null } }).discount_cap ?? null;
        setConfiguredDiscountCap(profile.role, servedCap);
        await cacheDiscountCap(servedCap);
        // The clock the till evaluates menu periods and combo windows against.
        setTenantTimezone(profile.tenant?.timezone);
        setLocationTimezones(profile.locations);
      } catch {
        // non-fatal — read path defaults to INR / tax-exclusive display
      }
    } catch {
      localStorage.removeItem(USER_KEY);
      set({
        user: null,
        profile: null,
        isAuthenticated: false,
      });
    }
  },

  hydrate: () => {
    const userJson = localStorage.getItem(USER_KEY);

    const adoptProfile = async (profile: UserProfile) => {
      // G2.27: re-establish the device session + encryption key
      // so IndexedDB reads/writes after a page reload stay usable.
      await beginDeviceSession();
      // Before the profile lands: billing renders on it, and the discount modal
      // reads this cap during that render (SS3-007).
      const cap = (profile as { discount_cap?: { percent: number; flat: number | null } }).discount_cap ?? null;
      setConfiguredDiscountCap(profile.role, cap);
      set({ profile, isLoading: false, isHydrated: true });
      // Multi-currency: refresh the offline currency cache on every
      // (re)hydrate so a re-stamped tenant currency propagates. Best-
      // effort — never block hydration.
      try {
        await cacheTenantCurrency(
          profile.tenant?.default_currency,
          profile.tenant_id ?? null,
        );
        await cacheTenantTaxInclusive(profile.tenant?.show_tax_inclusive === true);
        // CGST + SGST vs IGST per store, from the GST resolver (gst-parked D7).
        setLocationSupplyTypes(profile.locations);
        setTenantCurrency(profile.tenant?.default_currency);
        setCartGstRegistration(profile.tenant?.gst_registered, profile.tenant?.default_currency);
        setCashRoundingSettings(profile);
        await cacheDiscountCap(cap);
        setTenantTimezone(profile.tenant?.timezone);
        setLocationTimezones(profile.locations);
      } catch {
        // non-fatal — read path defaults to INR / tax-exclusive display
        // and the shipped discount caps
      }
      try {
        await useShiftStore.getState().loadActiveShift(profile.id, '');
      } catch {
        // non-fatal
      }
    };

    if (userJson) {
      try {
        const user = JSON.parse(userJson) as AuthUser;
        set({
          user,
          isAuthenticated: true,
          isLoading: true,
        });
        // Validate session by fetching profile (cookie sent automatically)
        authService
          .getMe()
          .then(adoptProfile)
          .catch(() => {
            localStorage.removeItem(USER_KEY);
            set({
              user: null,
              profile: null,
              isAuthenticated: false,
              isLoading: false,
              isHydrated: true,
            });
          });
      } catch {
        localStorage.removeItem(USER_KEY);
        set({ isLoading: false, isHydrated: true });
      }
    } else if (localStorage.getItem(LOGOUT_PENDING_KEY)) {
      set({ isLoading: false, isHydrated: true });
    } else {
      // Cross-app single sign-on (QA 2.3.20): a session started in the admin
      // app carries its cookies here — same host locally, COOKIE_DOMAIN-shared
      // subdomains when deployed — but not the admin origin's localStorage.
      // Adopt a live session instead of asking for a second login.
      set({ isLoading: true });
      authService
        .probeSession()
        .then(async (profile) => {
          const user: AuthUser = {
            sub: profile.id,
            email: profile.email,
            role: profile.role,
            tenant_id: profile.tenant_id,
          };
          localStorage.setItem(USER_KEY, JSON.stringify(user));
          set({ user, isAuthenticated: true });
          await adoptProfile(profile);
        })
        .catch(() => {
          localStorage.removeItem(USER_KEY);
          set({
            user: null,
            profile: null,
            isAuthenticated: false,
            isLoading: false,
            isHydrated: true,
          });
        });
    }
  },
}), { name: 'auth-store' }));
